import { useState } from "react";
import { Play } from "lucide-react";

// Self-hosted (not a YouTube embed): school networks often block YouTube, a
// third-party player would add tracking to the homepage, and same-origin media
// already satisfies the CSP. Files are versioned because public/ is served with
// a one-year cache header — bump the version when the video changes.
const VIDEO = "/videos/pagepledge-overview-v1";
export const OVERVIEW_VIDEO = {
  poster: `${VIDEO}-poster.jpg`,
  small: `${VIDEO}-720.mp4`,
  large: `${VIDEO}-1080.mp4`,
  captions: `${VIDEO}.en.vtt`,
  duration: "1:42",
  durationLabel: "1 minute 42 seconds",
};

/**
 * Hero video: a lightweight poster with a play button. Nothing but the poster
 * image loads until the visitor presses play; then the video plays inline
 * with sound, native controls, and English captions available.
 */
export default function HeroVideo({ className = "" }) {
  const [playing, setPlaying] = useState(false);

  return (
    <div
      className={`relative aspect-video rounded-2xl overflow-hidden border-[2.5px] border-foreground/90 bg-card shadow-[0_18px_48px_-12px_hsl(20_15%_12%/0.22)] has-[button:focus-visible]:ring-2 has-[button:focus-visible]:ring-ring has-[button:focus-visible]:ring-offset-2 ring-offset-background ${className}`.trim()}
    >
      {playing ? (
        <video
          className="absolute inset-0 h-full w-full bg-card"
          controls
          autoPlay
          playsInline
          preload="auto"
          poster={OVERVIEW_VIDEO.poster}
        >
          <source src={OVERVIEW_VIDEO.small} type="video/mp4" media="(max-width: 767px)" />
          <source src={OVERVIEW_VIDEO.large} type="video/mp4" />
          <track kind="captions" srcLang="en" label="English" src={OVERVIEW_VIDEO.captions} />
        </video>
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="group absolute inset-0 h-full w-full cursor-pointer focus-visible:outline-none"
          aria-label={`Play the PagePledge overview video (${OVERVIEW_VIDEO.durationLabel})`}
        >
          <img
            src={OVERVIEW_VIDEO.poster}
            alt=""
            width="1920"
            height="1080"
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <span className="absolute inset-x-0 bottom-[7%] flex justify-center">
            <span className="inline-flex items-center gap-3 rounded-full bg-primary py-2 pl-2 pr-5 text-sm font-semibold text-primary-foreground shadow-lg transition-colors group-hover:bg-primary-hover md:text-base">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/20">
                <Play className="h-4 w-4 translate-x-px fill-current" aria-hidden="true" />
              </span>
              Watch the overview
              <span className="font-normal opacity-80 font-tabular">{OVERVIEW_VIDEO.duration}</span>
            </span>
          </span>
        </button>
      )}
    </div>
  );
}
